import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  Renderer2,
  ViewChild,
  HostListener
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CompleterService } from 'ng2-completer';
import Swal from 'sweetalert2';

import { CanDeactivateGuard } from 'src/app/services/can-deactivate.guard';
import { EncriptacionService } from 'src/app/services/encriptacion.service';
import { GeneralService } from 'src/app/services/general.service';
import { PlansService } from 'src/app/services/plans.service';
import { ScheduleService } from 'src/app/services/schedule.service';
import { OndemandSocketService } from 'src/app/services/ondemand-socket.service';

import { PaymentButtonComponent } from 'src/app/components/payment-button/payment-button.component';

/** SNABB */
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  FonasaVoucherService,
  VoucherStatusResponse
} from 'src/app/services/fonasaSnabb.service';

@Component({
  selector: 'app-reserva-on-demand',
  templateUrl: './reserva-on-demand.component.html',
  styleUrls: ['./reserva-on-demand.component.css'],
})
export class ReservaOnDemandComponent implements OnInit, OnDestroy {

  @ViewChild('ErrorModal') trigErrorModal!: ElementRef;
  @ViewChild(PaymentButtonComponent) payment_button!: PaymentButtonComponent;

  // ✅ Para BYPASS del guard (igual que reserva-hora)
  skipDeactivateGuard = false;

  _usuario: any = {};  // usuario actual
  subtitle!: string;

  steps: any = {
    completeForm: false,
    payment: false,
    showSummary: false
  };

  // compat legado (tu lógica ya lo usa)
  paymentMethod = false;

  pageInfo: any = {};
  showSuccessMessage = false;

  compatibleSpecialities: any = [];
  planesDisponibles: any = {};

  especialidades_disponibles: any = [];

  specialitySelected: any = {};
  specialitySelectedValue: any = '';

  beneficiarySelected: any = {};

  planSelected: any = {};
  discountCode: any = '';
  discountCodeObject: any = {};

  paymentInfo: any = {
    originalValue: 0,
    inssuranceDiscount: null,
    inssuranceDiscountValue: 0,
    discountValue: 0,
    finalValue: 0,
    transaction: {
      url: null,
      token: null,
    },
  };

  tiempoEspera: any = {
    data: {
      horaPrimerPaciente: null,
      minutes: null,
      nroMedicos: null,
      posicion: null,
      tiempoEspera: null,
      tiempoEsperaTotal: null,
      totalPacientes: null
    }
  };

  consultaOndemand: any = {};
  totalPacientes: any;
  minutes: any;

  // ==========================
  // SNABB
  // ==========================
  isWaitingVoucher = false;
  voucherId?: string;

  selectedPaymentProvider: 'WEBPAY' | 'SNABB' | null = null;
  private voucherPollingSub?: Subscription;

  constructor(
    public generalService: GeneralService,
    private completerService: CompleterService,
    public scheduleService: ScheduleService,
    public router: Router,
    private renderer: Renderer2,
    public plansService: PlansService,
    private activatedRoute: ActivatedRoute,
    public confirmDialog: CanDeactivateGuard,
    private fb: FormBuilder,
    private ondemandSocketService: OndemandSocketService,
    private fonasaVoucherService: FonasaVoucherService,
  ) {
    this.pageInfo = new EncriptacionService().getPageInfo();
  }

  public onDemandForm = this.fb.group({
    area_medica: ['', Validators.required],
    paciente: ['', Validators.required],
    reason: ['', Validators.required],
    terminos: [false, Validators.requiredTrue],

    paymentInfo: [''],
    plan: [''],
    discountCode: [''],

    acceptTerms: [false, Validators.requiredTrue],

    // compat legado
    paymentMethod: [false],

    // NUEVO: provider reactivo
    paymentProvider: [null] // 'WEBPAY' | 'SNABB' | null
  });

  // =========================================================
  // LOADING helpers (NO cerrar antes de redirect)
  // =========================================================
  private showLoading(texto: string = 'Espere por favor...') {
    Swal.fire({
      html: `<div style="margin: 40px 0;">
        <i class="material-icons" style="color:#2BABB7; font-size: 58px; margin-bottom:25px">schedule</i>
        <h1 style="font-family: Mulish; font-weight: 700; font-size: 24px; text-align: center; color: #171994;">
          ${texto}
        </h1></div>`,
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });
  }

  private closeLoading() {
    try { Swal.close(); } catch (_) { }
  }

  private markRedirecting() {
    sessionStorage.setItem('od_redirecting', '1');
  }

  private clearRedirecting() {
    sessionStorage.removeItem('od_redirecting');
  }

  private cleanupAfterBackFromPayment(): void {
    this.isWaitingVoucher = false;
    this.stopVoucherPolling();
    this.closeLoading();

    // limpiar selección de pago para que no quede estado raro
    this.onDemandForm.get('paymentProvider')?.setValue(null);
    this.selectedPaymentProvider = null;

    // limpiar flag
    this.clearRedirecting();
  }

  // ✅ BFCache: cuando vuelves con "Atrás", el navegador restaura el DOM/JS tal como estaba
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent) {
    const wasRedirecting = sessionStorage.getItem('od_redirecting') === '1';

    // event.persisted = true => restaurado desde BFCache
    if (event?.persisted || wasRedirecting) {
      this.skipDeactivateGuard = true;
      this.cleanupAfterBackFromPayment();
      this.router.navigate(['/reserva-on-demand']);
    }
  }

  // ✅ Respaldo adicional
  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    const wasRedirecting = sessionStorage.getItem('od_redirecting') === '1';
    if (document.visibilityState === 'visible' && wasRedirecting) {
      this.skipDeactivateGuard = true;
      this.cleanupAfterBackFromPayment();
      this.router.navigate(['/reserva-on-demand']);
    }
  }

  ngOnInit(): void {
    this._usuario = new EncriptacionService().getUserInfo();
    if (!this._usuario || !this._usuario._id) {
      console.error('Usuario no encontrado');
      return;
    }

    this.postListUsersWaitList();

    this.ondemandSocketService.onDoctorDisponible().subscribe((data) => {
      if (data === null) return;
      this.postListUsersWaitList();
    });

    this.ondemandSocketService.onDoctorNoDisponible().subscribe((data) => {
      if (data === null) return;
      this.postListUsersWaitList();
    });

    this.ondemandSocketService.onActualizaFila().subscribe((data) => {
      if (data === null) return;
      this.postListUsersWaitList();
    });

    this.ondemandSocketService.onDoctorFinalizaConsulta().subscribe((data) => {
      if (data === null) return;
      this.postListUsersWaitList();
    });

    this.ondemandSocketService.onAsignaDoctor().subscribe((data) => {
      if (data === null) return;
      this.postAsignarMedicoPaciente();
    });

    this.showSuccessMessage = false;
    this.steps = {
      completeForm: true,
      payment: false,
      showSummary: false,
    };

    this.beneficiarySelected = this._usuario;

    this.onDemandForm.patchValue({
      paciente: this.beneficiarySelected._id
    });

    this.postListBeneficiariesXPlans();
    this.changeSubtitle(1);
  }

  ngOnDestroy(): void {
    this.stopVoucherPolling();
    this.closeLoading();
    // OJO: no hacemos clearRedirecting aquí siempre, porque si estás justo redirigiendo
    // y se destruye el componente, el flag igual sirve para detectar el back.
  }

  changeSubtitle(step: number) {
    switch (step) {
      case 1:
        this.subtitle = 'Completa tu formulario de atención';
        break;
      case 2:
        this.subtitle = 'Elige tu convenio';
        break;
      case 3:
        this.subtitle = 'Tu confirmación';
        break;
    }
  }

  checkStep() {
    this.steps.completeForm = true;
    this.steps.payment = false;
    this.steps.showSummary = false;

    this.changeSubtitle(1);

    // Limpieza mínima (no afecta lógica)
    this.onDemandForm.get('paymentProvider')?.setValue(null);
    this.selectedPaymentProvider = null;

    this.isWaitingVoucher = false;
    this.stopVoucherPolling();
    this.closeLoading();
  }

  onSubmit() { }

  postListBeneficiariesXPlans() {
    this.plansService.postListBeneficiariesXPlans(this._usuario.use_id || this._usuario._id)
      .then((data: any) => {

        data.forEach((element: any) => {
          if (element.use_id == this._usuario._id) {
            this.beneficiarySelected = element;
          }
        });

        const planes = this.beneficiarySelected.planes;

        if (data && data.length > 0 && planes) {
          const allSpecialities: any[] = [];
          data[0].planes.forEach((plan: any) => {
            if (plan.especialidades && plan.especialidades.length > 0) {
              allSpecialities.push(...plan.especialidades);
            }
          });

          this.compatibleSpecialities = allSpecialities.filter((value, index, self) =>
            index === self.findIndex((t) => t.spe_id === value.spe_id)
          );
        }
      })
      .catch((e: any) => console.error(e));
  }

  changeSpeciality(event: Event) {
    const selectedSpecialityId = (event.target as HTMLSelectElement).value;
    const selectedSpeciality = this.especialidades_disponibles.find(
      (speciality: any) => speciality.spe_id === parseInt(selectedSpecialityId, 10)
    );
    this.specialitySelected = selectedSpeciality || {};

    this.changeSpecialityDisp();
    this.calculatePaymentValues();
  }

  changeSpecialityDisp() {
    this.planesDisponibles = [];

    if (this.beneficiarySelected && this.beneficiarySelected.planes && this.specialitySelected.spe_id) {
      for (let index = 0; index < this.beneficiarySelected.planes.length; index++) {
        const element = this.beneficiarySelected.planes[index];

        if (element.especialidades && element.especialidades.length > 0) {
          for (let indice = 0; indice < element.especialidades.length; indice++) {
            const element2 = element.especialidades[indice];
            if (element2.spe_id == this.specialitySelected.spe_id) {
              this.planesDisponibles.push(element);
            }
          }
        }
      }
    }
  }

  redirectToStep2() {
    const areaMedicaValid = this.onDemandForm.get('area_medica')?.valid;
    const pacienteValid = this.onDemandForm.get('paciente')?.valid;
    const reasonValid = this.onDemandForm.get('reason')?.valid;
    const terminosValid = this.onDemandForm.get('terminos')?.valid;

    if (!areaMedicaValid || !pacienteValid || !reasonValid || !terminosValid) {
      this.generalService.modalError('Debes completar todos los campos para continuar.', '');
      return;
    }

    this.steps.completeForm = false;
    this.steps.payment = true;
    this.discountCodeObject = {};
    this.discountCode = '';
    this.changeSubtitle(2);
  }

  postListUsersWaitList() {
    this.scheduleService.postListUsersWaitList(this._usuario._id, this._usuario.typ_id)
      .then((response: any) => {
        this.tiempoEspera = response;
        this.consultaOndemand = response.consulta || {};
        this.especialidades_disponibles = response.especialidades || [];
      });
  }

  postAsignarMedicoPaciente() {
    this.scheduleService.asignarMedicoPaciente().then((_response: any) => { });
  }

  planRowSelected(plan: any) {
    if (this.planSelected?.ben_id === plan.ben_id) {
      this.planSelected = {};
      this.onDemandForm.get('plan')?.setValue('');
    } else {
      this.planSelected = plan;
      this.onDemandForm.get('plan')?.setValue(plan.ben_id);
    }

    this.calculatePaymentValues();
  }

  calculatePaymentValues() {
    const basePrice = parseInt(this.specialitySelected.sub_spe_pri_price || '0', 10);
    this.paymentInfo.originalValue = basePrice;

    this.paymentInfo.inssuranceDiscountValue = 0;
    this.paymentInfo.discountValue = 0;

    if (this.planSelected && this.planSelected.especialidades) {
      const especialidadPlan = this.planSelected.especialidades.find((e: any) => e.spe_id === this.specialitySelected.spe_id);
      if (especialidadPlan && especialidadPlan.porcentaje_descuento) {
        const porcentaje = especialidadPlan.porcentaje_descuento / 100;
        this.paymentInfo.inssuranceDiscountValue = basePrice * porcentaje;
      }
    }

    const subtotal = basePrice - this.paymentInfo.inssuranceDiscountValue;
    this.paymentInfo.finalValue = subtotal - this.paymentInfo.discountValue;
    if (this.paymentInfo.finalValue < 0) this.paymentInfo.finalValue = 0;
  }

  validarDcto() {
    this.discountCodeObject = {};
    const codigo = this.onDemandForm.value.discountCode;

    if (!codigo || (codigo as any).length < 16) {
      this.generalService.modalError('Código no válido', 'Ingrese el código nuevamente');
      return;
    }

    this.generalService.modalCargando();
    this.plansService.validateDiscounts(this.beneficiarySelected.use_id, codigo, this.onDemandForm.value.area_medica)
      .then((result: any) => {
        this.discountCodeObject = result;

        if (this.discountCodeObject.formula) {
          this.discountCodeObject.formula = this.discountCodeObject.formula.replaceAll('data.', '');
        }

        const basePrice = this.paymentInfo.originalValue;
        const inssuranceDiscount = this.paymentInfo.inssuranceDiscountValue;
        const subTotal = basePrice - inssuranceDiscount;

        let delta = 0;
        if (this.discountCodeObject.dis_typ_id === 1) {
          delta = subTotal * this.discountCodeObject.value;
        } else if (this.discountCodeObject.dis_typ_id === 2) {
          delta = this.discountCodeObject.value;
        }

        this.paymentInfo.discountValue = delta > subTotal ? subTotal : delta;
        this.paymentInfo.finalValue = subTotal - this.paymentInfo.discountValue;
        if (this.paymentInfo.finalValue < 0) this.paymentInfo.finalValue = 0;

        this.generalService.modalExitoso('Código válido', 'Descuento ingresado exitosamente.');
      })
      .catch((error: any) => {
        console.error(error);
        this.generalService.modalError(
          'Error al validar código',
          error?.error?.msg || 'Error al validar código de descuento'
        );
      });
  }

  async confirmarReservaOnDemand(transaction: any) {
    try {
      this.paymentInfo.transaction = transaction;
      await this.generalService.sleep(200);

      // ✅ NO cerrar loading aquí: redirige al portal
      this.payment_button.pay();
    } catch (error) {
      this.closeLoading();
      console.error('Error en confirmación de pago:', error);
      this.generalService.modalError('Error en el pago', 'No se pudo procesar el pago. Intenta más tarde.');
    }
  }

  async redirectToStep3() {
    const canPayByProvider = this.getAvailablePaymentProviders().length > 0;
    const paymentProvider = this.onDemandForm.get('paymentProvider')?.value as ('WEBPAY' | 'SNABB' | null);

    // ✅ Siempre debe aceptar T&C
    if (!this.onDemandForm.get('acceptTerms')?.value) {
      this.generalService.modalError('Debe aceptar los términos y condiciones para continuar.', '');
      return;
    }

    // ✅ Solo exigir selección de pago si hay monto > 0
    if ((this.paymentInfo.finalValue || 0) > 0) {
      if (canPayByProvider) {
        if (!this.hasValidPaymentSelection()) {
          this.generalService.modalError('Debes seleccionar un método de pago para continuar.', '');
          return;
        }
      } else {
        const aceptoMedioDePago = this.onDemandForm.get('paymentMethod')?.value;
        if (!this.planSelected?.ben_id && !aceptoMedioDePago) {
          this.generalService.modalError(
            'Falta información de pago',
            'Debes seleccionar un convenio o un medio de pago para continuar.'
          );
          return;
        }
      }
    }

    // ✅ OJO: tu backend usa "valorAPagar" como base - descuento convenio (sin código)
    const valorFinal = this.paymentInfo.originalValue - this.paymentInfo.inssuranceDiscountValue;

    const payload: any = {
      paciente: this._usuario._id,
      plan: this.planSelected?.ben_id || null,
      area_medica: this.onDemandForm.value.area_medica,
      reason: this.onDemandForm.value.reason,
      discountCode: this.onDemandForm.value.discountCode || null,
      valorAPagar: valorFinal,
      telefono: this._usuario.telefono,
      nombre_paciente: this._usuario.nombre_usuario,
      email: this._usuario.email,
      paymentMethod: valorFinal > 0 ? true : false,
      paymentProvider: (valorFinal > 0 ? paymentProvider : null) // ✅ si es 0, no forzar provider
    };

    try {
      // ✅ mostrar loading y mantenerlo hasta redirect o success
      this.showLoading(valorFinal > 0 ? 'Espere por favor...' : 'Ingresando a la fila…');

      const result: any = await this.scheduleService.postAddWaitList(payload);

      let consultaId = result?.data?.sp_crud_tel_doc_appointment_ondemand || null;

      if (!consultaId) {
        await this.postListUsersWaitList();
        consultaId = this.tiempoEspera?.consulta?.tel_doc_od_id;
      }

      if (!consultaId) {
        this.closeLoading();
        this.generalService.modalError('Error interno', 'No se pudo obtener el ID de la consulta.');
        return;
      }

      // ✅ Si NO hay pago (valor 0), pasar directo a resumen
      if ((this.paymentInfo.finalValue || 0) <= 0 || valorFinal <= 0) {
        this.clearRedirecting();
        this.closeLoading();
        this.steps.completeForm = false;
        this.steps.payment = false;
        this.steps.showSummary = true;
        this.changeSubtitle(3);
        this.showSuccessMessage = true;
        return;
      }

      // ✅ WEBPAY
      if (paymentProvider === 'WEBPAY' && result?.transaction?.url) {
        this.paymentInfo.transaction = result.transaction;

        this.skipDeactivateGuard = true;
        this.markRedirecting();

        await this.confirmarReservaOnDemand(result.transaction);
        return;
      }

      // ✅ SNABB
      if (paymentProvider === 'SNABB') {
        const voucherId =
          result?.transaction?.voucher_id ||
          result?.transaction?.voucherId ||
          result?.voucher_id ||
          result?.voucherId;

        if (!voucherId) {
          this.closeLoading();
          this.generalService.modalError('Error', 'No se pudo generar el voucher FONASA.');
          return;
        }

        this.voucherId = voucherId;
        this.isWaitingVoucher = true;

        // mantener loading hasta tener payment_url
        this.startVoucherPolling();
        return;
      }

      // ✅ Fallback
      this.closeLoading();
      this.steps.completeForm = false;
      this.steps.payment = false;
      this.steps.showSummary = true;
      this.changeSubtitle(3);
      this.showSuccessMessage = true;

    } catch (error) {
      console.error('Error al crear la consulta:', error);
      this.closeLoading();
      this.generalService.modalError('Error interno', 'No se pudo completar la reserva. Intenta más tarde.');
    }
  }

  async redirectToWaitingRoom() {
    try {
      this.showLoading('Espere por favor...');

      let intentos = 0;
      let consultaId = null;

      while (intentos < 5) {
        await this.postListUsersWaitList();
        consultaId = this.tiempoEspera?.consulta?.tel_doc_od_id;

        if (consultaId) break;

        intentos++;
        await this.generalService.sleep(1000);
      }

      if (consultaId) {
        const url = `fila-ondemand/${consultaId}/${consultaId}`;
        this.closeLoading();
        this.clearRedirecting();
        this.router.navigate([url]);
      } else {
        this.closeLoading();
        Swal.fire({
          icon: 'warning',
          title: 'Consulta no disponible',
          text: 'No hay una consulta ondemand disponible en este momento.',
          timer: 3000
        });
      }

    } catch (error) {
      console.error('Error al obtener la consulta:', error);
      this.closeLoading();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al obtener la consulta. Inténtalo nuevamente.',
        timer: 3000
      });
    }
  }

  // =========================================================
  // SNABB FONASA (polling) - mantener loading hasta payment_url
  // =========================================================
  private startVoucherPolling(): void {
    if (!this.voucherId) return;

    this.isWaitingVoucher = true;
    this.stopVoucherPolling();

    // por si algo cerró el modal, lo mantenemos
    this.showLoading('Generando bono FONASA…');

    this.voucherPollingSub = interval(3000)
      .pipe(
        switchMap(() => this.fonasaVoucherService.getVoucherStatus(this.voucherId as string))
      )
      .subscribe({
        next: (status: VoucherStatusResponse) => this.handleVoucherStatus(status),
        error: (err) => {
          console.error('Error consultando estado de voucher Fonasa', err);
          this.isWaitingVoucher = false;
          this.stopVoucherPolling();
          this.closeLoading();
          this.generalService.modalError('Error', 'No se pudo consultar el estado del voucher.');
        }
      });
  }

  private stopVoucherPolling(): void {
    if (this.voucherPollingSub) {
      this.voucherPollingSub.unsubscribe();
      this.voucherPollingSub = undefined;
    }
  }

  private handleVoucherStatus(status: VoucherStatusResponse): void {
    const currentStatus = status.status;

    const paymentUrl =
      status.payment_url && status.payment_url !== 'null'
        ? status.payment_url
        : null;

    // ✅ Redirigir solo cuando hay URL
    if (paymentUrl && currentStatus === 'Pending') {
      this.isWaitingVoucher = false;
      this.stopVoucherPolling();

      this.skipDeactivateGuard = true;
      this.markRedirecting();

      window.location.href = paymentUrl;
      return;
    }

    // Estados terminales de fallo
    if (['Canceled', 'Rejected', 'Expired'].includes(currentStatus)) {
      this.isWaitingVoucher = false;
      this.stopVoucherPolling();
      this.closeLoading();
      this.clearRedirecting();
      this.generalService.modalError('Voucher no disponible', 'No se pudo generar el pago con FONASA.');
      return;
    }

    // Estados terminales OK (depende de tu backend)
    if (['Payed', 'Done'].includes(currentStatus)) {
      this.isWaitingVoucher = false;
      this.stopVoucherPolling();
      this.closeLoading();
      this.clearRedirecting();

      this.steps.completeForm = false;
      this.steps.payment = false;
      this.steps.showSummary = true;
      this.changeSubtitle(3);
      this.showSuccessMessage = true;
      return;
    }

    // En otros estados, dejamos el loading abierto.
  }

  // =========================================================
  // HELPERS MEDIO DE PAGO
  // =========================================================
  getAvailablePaymentProviders(): Array<'WEBPAY' | 'SNABB'> {
    const providers: Array<'WEBPAY' | 'SNABB'> = [];
    if (this.pageInfo?.cus_can_pay) providers.push('WEBPAY');
    if (this.pageInfo?.cus_can_snabb) providers.push('SNABB');
    return providers;
  }

  hasValidPaymentSelection(): boolean {
    if ((this.paymentInfo?.finalValue || 0) <= 0) return true;

    const providers = this.getAvailablePaymentProviders();
    if (!providers.length) return false;

    const provider = this.onDemandForm.get('paymentProvider')?.value as ('WEBPAY' | 'SNABB' | null);

    if (providers.length === 1 && providers[0] === 'WEBPAY') {
      return !!this.onDemandForm.get('paymentMethod')?.value || provider === 'WEBPAY';
    }

    return provider === 'WEBPAY' || provider === 'SNABB';
  }

  // Mantén consistencia: variable + formControl
  togglePaymentProvider(provider: 'WEBPAY' | 'SNABB', event?: any) {
    if (event) {
      event.stopPropagation?.();
    }

    const paymentProviderCtrl = this.onDemandForm.get('paymentProvider');
    const paymentMethodCtrl = this.onDemandForm.get('paymentMethod');

    if (this.selectedPaymentProvider === provider) {
      this.selectedPaymentProvider = null;
      paymentProviderCtrl?.setValue(null);

      this.paymentMethod = false;
      paymentMethodCtrl?.setValue(false);

      paymentProviderCtrl?.markAsDirty();
      paymentProviderCtrl?.markAsTouched();
      paymentMethodCtrl?.markAsDirty();
      paymentMethodCtrl?.markAsTouched();
      return;
    }

    this.selectedPaymentProvider = provider;
    paymentProviderCtrl?.setValue(provider);

    this.paymentMethod = true;
    paymentMethodCtrl?.setValue(true);

    paymentProviderCtrl?.markAsDirty();
    paymentProviderCtrl?.markAsTouched();
    paymentMethodCtrl?.markAsDirty();
    paymentMethodCtrl?.markAsTouched();
  }
}
